/**
 * Mutation Manager
 *
 * Handles optimistic updates for database mutations with automatic rollback on failure.
 * Coordinates cache invalidation between custom cache layer and React Query (if present).
 */

import { cacheManager } from './cache-manager';

class MutationManager {
  constructor() {
    this.queryClient = null; // React Query client (optional)
  }

  /**
   * Set React Query client for coordinated cache invalidation
   * @param {QueryClient} client - React Query client instance
   */
  setQueryClient(client) {
    this.queryClient = client;
  }

  /**
   * Perform mutation with optimistic update and automatic rollback
   * @param {Object} options - Mutation options
   * @param {string} options.entity - Entity name (e.g., 'crops', 'organizations')
   * @param {string} options.operation - Operation type ('create', 'update', 'delete')
   * @param {*} options.optimisticData - Data for optimistic update
   * @param {Function} options.mutationFn - Async function that performs actual mutation
   * @param {Function} [options.onSuccess] - Success callback
   * @param {Function} [options.onError] - Error callback
   * @returns {Promise<{data, error}>}
   */
  async mutate({
    entity,
    operation,
    optimisticData,
    mutationFn,
    onSuccess,
    onError
  }) {
    // Store snapshot for rollback (not implemented in current cache manager)
    // For now, we'll just invalidate on both success and error

    try {
      // Perform the mutation
      const result = await mutationFn();

      if (result.error) {
        throw result.error;
      }

      // Success: invalidate cache to force fresh fetch
      this.invalidate(entity);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result.data);
      }

      return { data: result.data, error: null };

    } catch (error) {
      console.error(`[MUTATION ERROR] ${entity} ${operation}:`, error);

      // Call error callback if provided
      if (onError) {
        onError(error);
      }

      // Still invalidate cache to ensure consistency
      this.invalidate(entity);

      return { data: null, error };
    }
  }

  /**
   * Invalidate cache for entity (both custom cache and React Query)
   * @param {string} entity - Entity name
   */
  invalidate(entity) {
    // Invalidate custom cache layer
    cacheManager.invalidate(entity);

    // Invalidate React Query cache if available
    if (this.queryClient) {
      this.queryClient.invalidateQueries([entity]);
    }
  }

  /**
   * Invalidate multiple entities at once
   * @param {string[]} entities - Array of entity names
   */
  invalidateMultiple(entities) {
    entities.forEach(entity => this.invalidate(entity));
  }
}

// Export singleton instance
export const mutationManager = new MutationManager();

// Export class for testing
export { MutationManager };
